// @atlas/contracts — kereszt-vágó séma-építőkövek (több feature is használja)
//
// A fájl CSAK sémákat tart (érték-szintű Zod-objektumok + a belőlük inferált típusok) — a ROUTE-ok
// egyetlen `atlasContract` objektum-literálban maradnak (`src/index.ts`), mert a ts-rest
// kontextus-inferenciája a lapos bővítésre érzékeny. A sémák mozgatása ezt NEM érinti: sima értékek.
import { CLIENT_STATUSES } from '@atlas/shared';
import { z } from 'zod';
/** Egy resource-busz sor (generikus meta-alak; a `data` a namespace-séma szerinti). */
export const OdinResourceSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    data: z.unknown(),
    createdAt: z.string(),
});
export const ErrorSchema = z.object({ message: z.string() });
/** CRM ügyfél-státusz (a `@atlas/shared` business enumból) — a create/update szigorúan ezt fogadja. */
export const ClientStatusSchema = z.enum(CLIENT_STATUSES);
/**
 * Egy számlázási profil a lista „Számlázási adatok" oszlopához (legacy billing_profiles-parity). Rendezés:
 * `isPrimary` DESC, majd `billingName`. A cím a `zipCode`/`city`/`address`-ből áll össze a UI-ban; a
 * `countryCode` az adószám-chip (HU) forrása (a legacy fix „HU" helyett — döntés a feltárás alapján).
 */
export const ClientBillingProfileSchema = z.object({
    id: z.string(),
    billingName: z.string(),
    taxNumber: z.string().nullable(),
    euTaxNumber: z.string().nullable(),
    countryCode: z.string(),
    zipCode: z.string().nullable(),
    city: z.string().nullable(),
    address: z.string().nullable(),
    isPrimary: z.boolean(),
});
/** Validációs hiba-válasz (422): humán-üzenet + mező→hibaüzenetek (a zod-issue path a mezőre képezve). */
export const ValidationErrorSchema = z.object({
    message: z.string(),
    errors: z.record(z.array(z.string())),
});
export const ListQuerySchema = z.object({
    q: z.string().optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().positive().max(200).optional(),
});
// A SZEMÉLY-REF sémák (`AccountNamedRef`/`AccountPersonRef`) itt, a kontakt-blokk ELŐTT állnak, mert a
// `ContactRowSchema.admins` is ezt használja — egy `const` séma FORWARD hivatkozása TDZ-hibát adna a modul
// betöltésekor (a `z.object({...})` azonnal kiértékelődik), nem típus-hibát. Ezért a sorrend load-bearing.
/** Egy fiókhoz kötődő nevesített entitás (identity/szolgáltatás) — id + feloldott név. */
export const AccountNamedRefSchema = z.object({ id: z.string(), name: z.string() });
/**
 * SZEMÉLY-OPCIÓ egy legördülőhöz/pickerhez: id + fallback név + a STRUKTURÁLT név-részek. MIÉRT kell a két
 * extra mező: a névsorrend NYELV-FÜGGŐ (magyarul „Reizner Szilárd", angolul „Szilárd Reizner"), és az ABC-
 * rendezés is a MEGJELENÍTETT alakot követi — ezt a szerver nem tudja eldönteni, mert a nyelvet a felület
 * váltja futásidőben (ugyanaz a válasz cache-elve mindkét nyelven kiszolgál). A `name` a locale-független
 * fallback (displayName), a formázás/rendezés a kliens `formatName`/`sortPersonOptions` párja.
 */
export const PersonOptionSchema = z.object({
    id: z.string(),
    name: z.string(),
    givenName: z.string().nullable(),
    familyName: z.string().nullable(),
});
/**
 * Fiókhoz kötődő SZEMÉLY-referencia (ügyféladminisztrátor/kapcsolattartó): a nevesített ref + a
 * strukturált név-részek és a profilkép (presigned URL). A `name` marad locale-független fallbacknek
 * (displayName); a `givenName`/`familyName` a locale-tudatos névsorrendhez (HU: vezetéknév elöl), az
 * `avatarUrl` az avatar-képhez (nincs → monogram-fallback). Csak személy-refeknél (a `services` sima ref).
 */
export const AccountPersonRefSchema = AccountNamedRefSchema.extend({
    /**
     * `null`, ha az identity nem oldható fel (offboardolt/törölt személy) — a felület „Törölt felhasználó"
     * címkét mutat. SOHA nem a nyers UUID megy név-fallbackként (mérve: az ment).
     */
    name: z.string().nullable(),
    givenName: z.string().nullable(),
    familyName: z.string().nullable(),
    avatarUrl: z.string().nullable(),
    /** A személy CSAPAT-szín tokenje (MORGENS-paletta) — az avatar-háttérhez (belső kolléga); null → nincs. */
    teamColor: z.string().nullable(),
});
/**
 * Fiók-frissítés body — a `clients/account` Schema Registry `account` json_schema WRITABLE mezői (a szerver
 * az Odin buszra a NYERS data-objektumot küldi, `additionalProperties:false`). Minden mező opcionális
 * (részleges PATCH; a hiányzó mezőt a szerver a meglévő data-ból tartja meg). A `status` a CRM-státusz
 * (active/pending/inactive) — a `clients/account.data.status` mezője. Üres string → null (mező-törlés).
 * A `name` a séma egyetlen kötelező mezője; ha megadják, min 1 / max 255. A technikai/hivatkozás-mezők
 * (type/pms/bookingEngine/…) szabad string-slugok (a reference-data feloldás a UI dolga).
 */
export const accountsEmptyToNull = (v) => typeof v === 'string' && v.trim() === '' ? null : v;
export const nullableStr = (max) => z.preprocess(accountsEmptyToNull, z.string().max(max).nullable().optional());
/**
 * Ticket-lista lekérdezés: státusz- + típus-szűrő + szabad-szöveg (tárgy/jegyszám/fiók/felelős/igénylő) +
 * szerver-oldali (BFF) rendezés (`sort`/`order`) + offset-lapozás — mind a BFF-szűrt halmazon (a busz a
 * tipizált support.tickets táblán nem szűr/rendez). A rendezés-kulcsok: subject | ticketNumber | createdAt |
 * accountName | assigneeName | status (ismeretlen kulcs → createdAt DESC alapértelmezett). API-parity a többi listával.
 */
/**
 * ÉLETCIKLUS-CSOPORTOK a ticket-listán — KÉT csoport: aktív és lezárt.
 *   • `in_progress` = `open` + `in_progress` + `reopened` · `closed` = `closed` + `resolved`.
 *
 * A legacy HÁROM fület adott (`opened` külön), és az Atlas is így indult. A külön „Nyitott" fül azonban
 * FÖLÖSLEGES döntést kért a szkennelés előtt (user-döntés: „eleg a folyamatban es a lezart tabok,
 * a statusz badge-ek pedig pontosan jelzik, hogy epp hogy is all"): a sor STÁTUSZ-JELVÉNYE amúgy is kiírja,
 * hogy a jegy `nyitott`, `folyamatban` vagy `újranyitott`, a finomabb szűkítést pedig a `status` szűrő adja.
 * Így a partner-portál fülezésével is EGYEZIK (`portal-tickets.data.ts` `TAB_STATUSES`) — egy mentális modell
 * a két felületen.
 *
 * A halmazok TELJESEK és diszjunktak: minden státusz PONTOSAN egy csoportba esik, tehát egy jegy sem tud
 * kiesni a listáról (ez a fül-kivezetés valódi kockázata volt).
 */
export const TICKET_GROUP_STATUSES = {
    in_progress: ['open', 'in_progress', 'reopened'],
    closed: ['closed', 'resolved'],
};
/** WYSIWYG-kép feltöltés-kérés: a legacy validálásával egyező (kép-típus + 5 MB) + a CÉL-TENANT. */
/**
 * A SZERKESZTŐBE BESZÚRHATÓ MÉDIA — egyetlen, közös szabály (típus-whitelist + típusonkénti méret-plafon).
 *
 * MIÉRT EGY HELYEN, ÉS MIÉRT ADAT: a szabályt HÁROM helyen kell érvényesíteni — a belső feltöltő-végponton, a
 * PARTNER-oldali párján, és a kliensen (azonnali visszajelzés). Három kézzel írt lista garantáltan elcsúszik;
 * a legelső ilyen csúszás a felhasználónál „nem működik a feltöltés"-ként jelenik meg (a kliens elfogadja,
 * a szerver 422-t ad).
 *
 * A KÉP-TÍPUSOK a legacy `mimes:jpg,jpeg,png,gif,webp` szabályát követik. A VIDEÓ (user-kérés:
 * rövid képernyőfelvételek) SZŰK készlet: `mp4` (H.264 — a de facto univerzális), `webm` és `quicktime`
 * (`.mov`, a macOS-képernyőfelvétel alapja). Ami SZÁNDÉKOSAN nincs: `avi`/`mkv`/`wmv` — a böngészők nem
 * játsszák le őket megbízhatóan, tehát a feltöltés „siker" lenne, a lejátszás mégis fekete téglalap.
 *
 * A MÉRET-PLAFON TÍPUSONKÉNT MÁS, és ez nem finomhangolás: 5 MB-os videó-plafon mellett egy 20 másodperces
 * képernyőfelvétel sem menne át (vagyis a funkció nem létezne), 25 MB-os KÉP-plafon mellett viszont egy
 * félrekattintott RAW-export is bekerülhetne a szálba.
 */
export const EDITOR_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
export const EDITOR_VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];
export const EDITOR_MEDIA_TYPES = [...EDITOR_IMAGE_TYPES, ...EDITOR_VIDEO_TYPES];
/** KÉP-plafon: a legacy `max:5120` (kB) = 5 MB. */
export const EDITOR_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
/**
 * VIDEÓ-plafon: 25 MB. Ez néhány tíz másodperc képernyőfelvétel (a `FILE_MAX_BYTES` platform-plafon 50 MB,
 * tehát bőven alatta maradunk), és nem hívja fel a szálat félgigás nyers felvételekkel.
 */
export const EDITOR_VIDEO_MAX_BYTES = 25 * 1024 * 1024;
/** Az adott MIME-típushoz tartozó méret-plafon (a típus-whitelisten kívüli értékre a kép-plafon a szűkebb). */
export function editorMediaMaxBytes(contentType) {
    return EDITOR_VIDEO_TYPES.includes(contentType)
        ? EDITOR_VIDEO_MAX_BYTES
        : EDITOR_IMAGE_MAX_BYTES;
}
export const EditorImageUploadBodySchema = z
    .object({
    /**
     * A kép CÉL-FIÓKJA. Az Odin fájl-metaadata tenant-kötött (`client_id NOT NULL`), tehát a feltöltés nem lehet
     * „ügyfél nélküli": a szerkesztőben beszúrt kép ANNAK az ügyfélnek az adata, akinek a ticketje/üzenete
     * hordozza — így az RLS védi, és az ügyfél törlésével a képei is mennek.
     *
     * A kliens a FIÓKOT adja meg, NEM az ügyfelet: a cél-tenant a fiók forrásából oldódik fel a szerveren
     * (`resolveAccountClientId`), így nem hamisítható meg egy idegen ügyfélre.
     *
     * OPCIONÁLIS: fiók-kontextus NÉLKÜL is lehet képet beszúrni — ilyenkor a fájl gazdája a BELSŐ
     * (MORGENS) ügyfél-rekord, ugyanaz a tenant, ami az üzenet-mellékleteket is tartja. MIÉRT: a szerkesztő
     * GLOBÁLIS komponens, és van felület, ahol nincs (és nem is lehet) cél-ügyfél — pl. a „mindenkinek" szóló
     * körlevél. A korábbi kötelező mező miatt a kép-gomb ott meg sem jelent, a jegy-formon pedig fiók-választás
     * előtt TILTOTT volt (a user „nem működik"-ként jelentette).
     */
    accountId: z.string().uuid().optional(),
    filename: z.string().trim().min(1).max(255),
    /** KÉP vagy rövid VIDEÓ — a közös `EDITOR_MEDIA_TYPES` whitelist. */
    contentType: z.enum(EDITOR_MEDIA_TYPES),
    /** Bájt-méret; a PLAFON típus-függő (lásd a `superRefine`-t alább). */
    sizeBytes: z.number().int().positive(),
})
    // A MÉRET-PLAFON a TÍPUSTÓL függ (kép 5 MB, videó 25 MB), ezért nem lehet statikus `.max()`: a hibát a
    // `sizeBytes` MEZŐRE tesszük, hogy a felület a mező alatt tudja megmutatni.
    .superRefine((v, ctx) => {
    const max = editorMediaMaxBytes(v.contentType);
    if (v.sizeBytes > max) {
        ctx.addIssue({
            code: z.ZodIssueCode.too_big,
            maximum: max,
            type: 'number',
            inclusive: true,
            path: ['sizeBytes'],
            message: `A fájl mérete meghaladja a ${String(Math.round(max / (1024 * 1024)))} MB-os korlátot.`,
        });
    }
});
/** A presigned PUT-URL + a fájl-azonosító (a commit ezzel hívható). */
export const EditorImageUploadUrlSchema = z.object({
    uploadUrl: z.string(),
    fileId: z.string(),
});
/**
 * A commit is tenant-kontextust igényel (az Odin írás-kontextusa cél-ügyfelet kér) — szintén FIÓK-azonosítóval,
 * és szintén OPCIONÁLISAN (hiányában a BELSŐ ügyfél a fájl gazdája; lásd a feltöltő-séma doc-ját).
 */
export const EditorImageCommitBodySchema = z.object({
    accountId: z.string().uuid().optional(),
});
/** A commit válasza: a HTML-be MENTHETŐ, STABIL megjelenítő-URL (nem presigned — az lejárna). */
export const EditorImageCommitSchema = z.object({ url: z.string() });
/** A megjelenítés feloldása: FRISS presigned GET-URL a stabil úthoz (a HTML-be SOHA nem ez kerül). */
export const EditorImageResolveSchema = z.object({ url: z.string() });
/** Deviza (a legacy CurrencyEnum 1:1) — ISO-4217. */
export const CurrencySchema = z.enum(['HUF', 'EUR', 'USD']);
/** Számlázási típus (a legacy BillingTypeEnum 1:1). `one_time` → pricingType kényszerítve `flat`. */
export const BillingTypeSchema = z.enum(['recurring', 'one_time']);
/** NAV ÁFA-kód (a legacy VatRateEnum 1:1). */
export const VatRateSchema = z.enum([
    'VAT_27',
    'VAT_18',
    'VAT_5',
    'VAT_0',
    'AAM',
    'TAM',
    'FAA',
    'EUFAD37',
]);
/** Használat-forrás típus (a legacy UsageSourceTypeEnum 1:1; csak tiered/base_tiered/commission plannél). */
export const UsageSourceTypeSchema = z.enum([
    'newsletter',
    'loyalty_newsletter',
    'rs_loyalty',
    'rs_direct_referral',
    'rs_direct',
    'rs_referral',
    'rs_voucher',
    'ticket',
]);
/**
 * A JUTALÉKOS használat-források (a `UsageSourceTypeSchema` jutalékos tagjai) — EGY helyen: a jutalék-lekérés
 * ezekre ír sort, a generátor pedig ezeknél csak a HotelCMS-azonosítót fogadja el (a jutalék-riport kulcsa a
 * HotelCMS `domains_v2.id`; ZadirCMS-tartalék itt nincs).
 */
// a lista forrás-igazsága a `@atlas/shared` (a mért-azonosító szabály — `usageSourceSystems` — is onnan olvassa)
export { COMMISSION_USAGE_SOURCE_TYPES } from '@atlas/shared';
/**
 * Eseménynapló-sor (lista-nézet) — PII-fegyelem: a `before`/`after` JSON SOHA nem része a listának,
 * csak a metaadat (mikor–ki–mit–min). A `status` a legacy siker/hiba jelző (élő Odin-eseménynél null).
 */
/** Egy mezőszintű változás (régi→új) — a nyers before/after SOHA nem jön ki, csak ez a REDAKÁLT diff. */
export const ActivityChangeSchema = z.object({
    field: z.string(),
    /** A régi/új érték tetszőleges JSON (skalár/objektum/tömb/null) — a titok-mezők szerver-oldalon redaktálva. */
    old: z.unknown(),
    new: z.unknown(),
});
export const ActivityEventSchema = z.object({
    id: z.string(),
    occurredAt: z.string(),
    action: z.string(),
    /** Derivált kategória (az action-ből): data | admin | auth_security | auth_session | security | system. */
    category: z.string(),
    status: z.string().nullable(),
    actorId: z.string().nullable(),
    actorName: z.string().nullable(),
    actorEmail: z.string().nullable(),
    /** Név-részek a locale-tudatos névsorhoz (hu → „Vezetéknév Keresztnév"). */
    actorGivenName: z.string().nullable(),
    actorFamilyName: z.string().nullable(),
    /** Belső aktor szines profilképe (presigned) + csapat-szín — a valós avatarhoz; egyébként null → monogram. */
    actorAvatarUrl: z.string().nullable(),
    actorTeamColor: z.string().nullable(),
    /** MEGSZEMÉLYESÍTÉS: ha kitöltve, az `actor*` a VALÓDI aktor, ez pedig az, akinek a nevében dolgozott.
     *  A név `null` maradhat (törölt identity) — az id jelenléte önmagában is jelzés a felületnek. */
    impersonatedId: z.string().nullable(),
    impersonatedName: z.string().nullable(),
    /** A megszemélyesített NÉV-RÉSZEI — a sorrendet a felület rendeli (`formatName`): magyarul vezetéknév elöl.
     *  Az összefűzött `impersonatedName` a fallback (a migrált adatban „keresztnév vezetéknév" alakú, ami magyar
     * felületen fordítva jelent meg — user-lelet). Régi/törölt identitáson mindhárom lehet `null`. */
    impersonatedGivenName: z.string().nullable(),
    impersonatedFamilyName: z.string().nullable(),
    /** Az érintett SZEMÉLY (admin-események identity-ref-jéből feloldva) — a nyers UUID helyett; a UI `formatName`-je
     *  rakja locale-sorrendbe (given/family + displayName fallback). */
    targetName: z.string().nullable(),
    targetGivenName: z.string().nullable(),
    targetFamilyName: z.string().nullable(),
    /** Az érintett entitás KONKRÉT neve(i) a legacy lang_attributes-ból (pl. „Melea…", „#TKT-…"); natívnál null. */
    subjectName: z.string().nullable(),
    subjectDetail: z.string().nullable(),
    /** Ha az érintett SZEMÉLY: a név-részek (a UI `formatName`-je rakja locale-sorrendbe, mint a szereplőnél). */
    subjectGivenName: z.string().nullable(),
    subjectFamilyName: z.string().nullable(),
    clientId: z.string().nullable(),
    clientName: z.string().nullable(),
    accountId: z.string().nullable(),
    accountName: z.string().nullable(),
    resourceRef: z.string().nullable(),
    appKey: z.string().nullable(),
    /** Request-metaadat (forenzika, főleg auth-eseménynél) — a detail-panelbe; a válasz superadmin-only. */
    ip: z.string().nullable(),
    userAgent: z.string().nullable(),
    /** OTel korrelációs id (ugrás a teljes hibához) + rövid, olvasható hiba-ok (sikertelen esemény); nyers stacktrace SOHA. */
    traceId: z.string().nullable(),
    errorText: z.string().nullable(),
    /** Mezőszintű régi→új diff (update/archive; create/delete-nél üres) — REDAKÁLT, PII/titok-mentes. */
    changes: z.array(ActivityChangeSchema),
});
// ── RBAC admin (az Odin admin API proxyja — a token atlas:admin / platform:admin jogán) ──
/** Kétnyelvű (hu/en) címke — a szerep/jog megjelenített neve (az Odin `label Json?`-jából). */
export const I18nLabelSchema = z.object({ hu: z.string(), en: z.string() });
/** Nem-negatív Decimal-STRING (max 2 tizedes) — pénz/mennyiség a dráton (SOHA number). */
export const decimalStr = z
    .string()
    .regex(/^\d+(\.\d{1,2})?$/, 'Érvénytelen tizedes-érték (max 2 tizedes).');
/** Korlátozott nem-negatív Decimal-STRING: max 8 egész számjegy + 2 tizedes (≤ 99 999 999.99) — a komponens-fa
 *  ár/mennyiség felső korlátja, hogy az érték (és a szülő Σ) a busz `Decimal(12,2)` tartományában maradjon. */
export const boundedDecimalStr = z
    .string()
    .regex(/^\d{1,8}(\.\d{1,2})?$/, 'Érvénytelen érték (max 8 egész számjegy, 2 tizedes).');
/**
 * TÖMB-query-paraméter normalizálása `z.preprocess`-hez: egyetlen érték → egyelemű tömb, tömb → változatlan, hiány →
 * `undefined`. A kliens a több-választós szűrőt ismételt paraméterként küldi, de egy kiválasztásnál egyetlen érték
 * érkezik (handbook §5). Használat: `z.preprocess(toQueryArray, z.array(…)).optional()`.
 */
export const toQueryArray = (v) => v === undefined ? undefined : Array.isArray(v) ? v : [v];
/** ISO date-string (YYYY-MM-DD) — `@db.Date` dátumhatár, NE alakuljon UTC-időre. */
export const isoDateStr = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Érvénytelen dátum (YYYY-MM-DD).');
/** A gyerek-lista felső határa — az Odin Schema Registry `maxItems`-ével egyező. */
export const MAX_CHILDREN = 20;
/**
 * Egy munkatárs GYEREKE (a `workforce.employee_details.children` eleme) — teljes név + születési dátum, MINDKETTŐ
 * kötelező. A munkatárs-adatlap (HR) és a belső Profilom (a dolgozó maga) is ezt írja.
 */
export const PersonChildSchema = z.object({
    fullName: z.string().trim().min(1).max(200),
    // saját (beégetett) üzenet NÉLKÜL — a felület a mezőt a saját, lokalizált hibaüzenet-térképével feliratozza.
    // VALÓS naptári nap kell (a `2026-02-30` is bukik) — ugyanaz a szabály, mint az Odin önkiszolgáló útján.
    birthDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .refine((v) => {
        const d = new Date(`${v}T00:00:00.000Z`);
        return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
    }),
});
/** Üres string → null (mező-törlés); HIÁNYZÓ (undefined) → undefined (a partial-update „nem küldött"
 *  detektálásához — a szerkesztés-lock ezen múlik: undefined = nem érkezett, null = explicit törlés). */
export const nullableIsoDate = z.preprocess((v) => (v === '' || v === null ? null : v), isoDateStr.nullable().optional());
//# sourceMappingURL=schemas.js.map